import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { downloadApiFile } from "../lib/downloadApiFile";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth, hasPermission } from "../context/AuthContext";
import { ArrowLeft, Loader2, RefreshCw, AlertTriangle, ShieldCheck, Download, CalendarClock } from "lucide-react";
import { formatInr } from "../lib/accountsSettlement";
import { isBackgroundJobResponse, backgroundJobQueuedToast } from "../lib/backgroundJobs";
import { useBackgroundJobResult } from "../hooks/useBackgroundJobNotifications";

function formatTdsInr(n) {
  return `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
}

function defaultDepositMonthValue() {
  const d = new Date();
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

function defaultFyQuarter() {
  const d = new Date();
  const m = d.getMonth() + 1;
  const fy = m >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  let q = 1;
  if (m >= 7 && m <= 9) q = 2;
  else if (m >= 10 && m <= 12) q = 3;
  else if (m <= 3) q = 4;
  return { fy, q };
}

function statusTone(status) {
  if (status === "OVERDUE") return "danger";
  if (status === "DUE_SOON") return "warning";
  if (status === "OK" || status === "CLOSED") return "success";
  return "default";
}

export default function TdsRegisterPage() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "tds.register.manage");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [natures, setNatures] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [depositPreview, setDepositPreview] = useState(null);
  const [returnPreview, setReturnPreview] = useState(null);
  const [applicableOnly, setApplicableOnly] = useState(false);
  const [section, setSection] = useState("");
  const [panFilter, setPanFilter] = useState("");
  const [backfilling, setBackfilling] = useState(null);
  const [backfillAllBusy, setBackfillAllBusy] = useState(false);
  const [refreshAllBusy, setRefreshAllBusy] = useState(false);
  const [pendingRefreshJobId, setPendingRefreshJobId] = useState("");
  const [depositMonth, setDepositMonth] = useState(defaultDepositMonthValue);
  const [returnFy, setReturnFy] = useState(() => defaultFyQuarter().fy);
  const [returnQuarter, setReturnQuarter] = useState(() => defaultFyQuarter().q);
  const [excelBusy, setExcelBusy] = useState("");
  const [payableTags, setPayableTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagsDirty, setTagsDirty] = useState(false);
  const [tagsSaving, setTagsSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { applicable_only: applicableOnly, limit: 1000 };
      if (section) params.section = section;
      if (panFilter.trim()) params.pan_no = panFilter.trim();
      const [reg, cov, nat, cal, tags] = await Promise.all([
        api.get("/tds/register", { params }),
        api.get("/tds/coverage"),
        api.get("/tds/natures"),
        api.get("/tds/compliance/calendar"),
        api.get("/tds/payable-tags"),
      ]);
      setRows(reg.data?.rows || []);
      setSummary(reg.data);
      setCoverage(cov.data);
      setNatures(nat.data?.natures || []);
      setCalendar(cal.data);
      setPayableTags(tags.data?.rows || []);
      setTagOptions(tags.data?.options || nat.data?.payable_tag_options || []);
      setTagsDirty(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [applicableOnly, section, panFilter]);

  useBackgroundJobResult(pendingRefreshJobId, async (event) => {
    setPendingRefreshJobId("");
    setRefreshAllBusy(false);
    if (event.action === "completed") {
      const result = event.result || {};
      const gaps = result.gaps || {};
      if ((result.party_errors || []).length || (gaps.errors || []).length) {
        toast.warning("Some reassess steps failed — review register after refresh");
      }
      await reload();
    }
  });

  const updatePayableTag = (payableCode, tdsNature) => {
    setPayableTags((prev) =>
      prev.map((r) =>
        r.payable_code === payableCode
          ? { ...r, tds_nature: tdsNature, tds_applicable: tdsNature !== "NONE" }
          : r
      )
    );
    setTagsDirty(true);
  };

  const savePayableTags = async () => {
    if (!canManage || !tagsDirty) return;
    setTagsSaving(true);
    try {
      const { data } = await api.put("/tds/payable-tags", {
        rows: payableTags.map((r) => ({
          payable_code: r.payable_code,
          tds_nature: r.tds_nature,
          label: r.label,
        })),
      });
      setPayableTags(data?.rows || payableTags);
      setTagOptions(data?.options || tagOptions);
      setTagsDirty(false);
      toast.success("Payable TDS tags saved");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setTagsSaving(false);
    }
  };

  const loadDepositPreview = useCallback(async () => {
    const [y, m] = String(depositMonth || "").split("-").map(Number);
    if (!y || !m) return;
    try {
      const { data } = await api.get("/tds/deposit-summary", { params: { year: y, month: m } });
      setDepositPreview(data);
    } catch (err) {
      toast.error(extractError(err));
    }
  }, [depositMonth]);

  const loadReturnPreview = useCallback(async () => {
    try {
      const { data } = await api.get("/tds/return-details", {
        params: { fy_start_year: returnFy, quarter: returnQuarter },
      });
      setReturnPreview(data);
    } catch (err) {
      toast.error(extractError(err));
    }
  }, [returnFy, returnQuarter]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    loadDepositPreview();
  }, [loadDepositPreview]);

  useEffect(() => {
    loadReturnPreview();
  }, [loadReturnPreview]);

  const gapCount = coverage?.gap_count ?? 0;

  const backfillFreight = async (voucherId) => {
    if (!canManage) return;
    setBackfilling(voucherId);
    try {
      const { data } = await api.post(`/tds/backfill-freight/${voucherId}`);
      toast.success(
        data?.tds_applicable
          ? `Assessed — TDS ₹${Number(data.tds_amount || 0).toLocaleString("en-IN")}`
          : `Assessed — ${data?.reason || "not applicable"}`,
      );
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBackfilling(null);
    }
  };

  const backfillAllGaps = async () => {
    if (!canManage) return;
    setBackfillAllBusy(true);
    try {
      const { data } = await api.post("/tds/backfill-gaps");
      toast.success(
        `Backfilled freight ${data?.freight || 0}, material ${data?.material || 0}, labour ${data?.labour || 0}`,
      );
      if ((data?.errors || []).length) {
        toast.warning(`${data.errors.length} gap(s) failed — see coverage after refresh`);
      }
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBackfillAllBusy(false);
    }
  };

  const refreshAllAssessments = async () => {
    if (!canManage) return;
    setRefreshAllBusy(true);
    let queued = false;
    try {
      const { data } = await api.post("/tds/refresh-assessments");
      if (isBackgroundJobResponse(data)) {
        const { title, message } = backgroundJobQueuedToast(data.job);
        toast.message(title, { description: message });
        setPendingRefreshJobId(data.job.job_id);
        queued = true;
        return;
      }
      const gaps = data?.gaps || {};
      toast.success(
        `Refreshed — gaps: freight ${gaps.freight || 0}, material ${gaps.material || 0}; parties reassessed ${data?.parties_reassessed || 0}`,
      );
      if ((data?.party_errors || []).length || (gaps.errors || []).length) {
        toast.warning("Some reassess steps failed — review register after refresh");
      }
      await reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      if (!queued) setRefreshAllBusy(false);
    }
  };

  const downloadDepositExcel = async () => {
    const [y, m] = String(depositMonth || "").split("-").map(Number);
    if (!y || !m) return;
    setExcelBusy("deposit");
    try {
      await downloadApiFile(
        "/tds/deposit-summary/excel",
        `tds-deposit-${y}-${String(m).padStart(2, "0")}.xlsx`,
        { year: y, month: m },
      );
      toast.success("Deposit summary Excel downloaded");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExcelBusy("");
    }
  };

  const downloadReturnExcel = async () => {
    setExcelBusy("return");
    try {
      await downloadApiFile(
        "/tds/return-details/excel",
        `tds-form140-FY${returnFy}-Q${returnQuarter}.xlsx`,
        { fy_start_year: returnFy, quarter: returnQuarter },
      );
      toast.success("Form 140 return details Excel downloaded");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExcelBusy("");
    }
  };

  const downloadPartywiseAnalysis = async () => {
    setExcelBusy("partywise");
    try {
      const params = {};
      if (summary?.fy_start) params.fy_start = summary.fy_start;
      if (summary?.fy_end) params.fy_end = summary.fy_end;
      await downloadApiFile(
        "/tds/party-analysis/export/excel",
        `tds-partywise-analysis-${String(summary?.fy_start || "").slice(0, 4) || "fy"}.xlsx`,
        params,
      );
      toast.success("Party-wise TDS analysis downloaded");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExcelBusy("");
    }
  };

  const fyOptions = useMemo(() => {
    const base = defaultFyQuarter().fy;
    return [base - 1, base, base + 1];
  }, []);

  const sectionOptions = useMemo(() => {
    const set = new Set();
    natures.forEach((n) => {
      if (n.payment_code) set.add(n.payment_code);
      if (n.payment_code_individual_huf) set.add(n.payment_code_individual_huf);
      if (n.payment_code_other) set.add(n.payment_code_other);
      if (n.section) set.add(n.section);
    });
    rows.forEach((r) => {
      if (r.payment_code || r.section) set.add(r.payment_code || r.section);
    });
    return Array.from(set).sort();
  }, [natures, rows]);

  return (
    <div className="space-y-4 pb-10">
      <PageHeader
        title="TDS register"
        description="Income-tax Act 2025 — payment codes 1023/1024 (freight & labour), 1006 (commission), 1031 (purchase of goods), filed in Form 140. Every withholdable payment is assessed — including below-threshold and exempt."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/accounts">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Accounts
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1">Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={excelBusy === "partywise"}
              onClick={downloadPartywiseAnalysis}
              data-testid="tds-partywise-analysis-export"
            >
              {excelBusy === "partywise" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              <span className="ml-1">Party-wise analysis</span>
            </Button>
            {canManage ? (
              <Button
                variant="outline"
                size="sm"
                disabled={refreshAllBusy || backfillAllBusy}
                onClick={refreshAllAssessments}
                data-testid="tds-refresh-assessments"
              >
                {refreshAllBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                <span className="ml-1">Reassess all FY</span>
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SectionCard title="FY assessments" className="min-w-0">
          <p className="text-2xl font-semibold tabular-nums">{summary?.count ?? "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {summary?.fy_start} → {summary?.fy_end}
          </p>
        </SectionCard>
        <SectionCard title="TDS applicable" className="min-w-0">
          <p className="text-2xl font-semibold tabular-nums">{summary?.applicable_count ?? "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">Rows with tax withheld</p>
        </SectionCard>
        <SectionCard title="Total TDS" className="min-w-0">
          <p className="text-2xl font-semibold tabular-nums">{formatTdsInr(summary?.total_tds || 0)}</p>
          <p className="text-xs text-muted-foreground mt-1">Payable this FY (register)</p>
        </SectionCard>
        <SectionCard title="Coverage" className="min-w-0">
          <div className="flex items-center gap-2">
            {gapCount === 0 ? (
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            <p className="text-2xl font-semibold tabular-nums">{gapCount}</p>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {gapCount === 0 ? "No unassessed finalized sources" : "Gaps need backfill"}
          </p>
        </SectionCard>
      </div>

      {summary && (
        <p className="text-xs text-muted-foreground">
          Code 1031 (purchase of goods) buyer gate:{" "}
          <Pill tone={(summary.buyer_1031_enabled ?? summary.buyer_194q_enabled) ? "success" : "default"}>
            {(summary.buyer_1031_enabled ?? summary.buyer_194q_enabled) ? "ON" : "OFF"}
          </Pill>
          {" · "}
          Return form: <Pill tone="info">140</Pill>
          {" · "}
          Set <code className="text-[11px]">TDS_1031_ENABLED</code> in backend .env if turnover gate changes.
        </p>
      )}

      <SectionCard
        title="Compliance desk — deposit & Form 140"
        subtitle="Deposit by 7th of next month · Quarterly Form 140 by the month after quarter-end (Q4: 31 May)"
        testId="tds-compliance-desk"
        action={<CalendarClock className="h-4 w-4 text-muted-foreground" />}
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-border/80 p-3 space-y-3 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">Monthly tax deposit</p>
                <p className="text-[11px] text-muted-foreground">
                  Challan format: 0021 other-than-company · 0020 corporation (+ detailed Excel sheet)
                </p>
              </div>
              {depositPreview?.status ? (
                <Pill tone={statusTone(depositPreview.status)}>{depositPreview.status}</Pill>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="text-[11px] text-muted-foreground">Deduction month</label>
                <Input
                  type="month"
                  className="h-8 text-xs w-40"
                  value={depositMonth}
                  onChange={(e) => setDepositMonth(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={excelBusy === "deposit"}
                onClick={downloadDepositExcel}
              >
                {excelBusy === "deposit" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                <span className="ml-1">Excel — deposit summary</span>
              </Button>
            </div>
            {depositPreview ? (
              <div className="text-xs space-y-2">
                <p>
                  Due <span className="font-medium">{depositPreview.deposit_due_date}</span>
                  {" · "}Total tax deposit {formatInr(depositPreview.total_tax_deposit ?? depositPreview.total_tds)}
                  {" · "}
                  {depositPreview.section_count} line(s)
                </p>
                {(depositPreview.sections || []).length === 0 ? (
                  <p className="text-muted-foreground">No applicable TDS in this month.</p>
                ) : (
                  <div className="space-y-3">
                    {[
                      depositPreview.other_than_company,
                      depositPreview.corporation,
                    ]
                      .filter((b) => b && (b.lines || []).length > 0)
                      .map((block) => (
                        <div key={block.major_head_code} className="space-y-1">
                          <p className="font-medium text-[11px]">
                            {block.title}{" "}
                            <span className="text-muted-foreground font-normal">
                              ({block.major_head_code})
                            </span>
                          </p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="text-muted-foreground border-b">
                                  <th className="text-left py-1 pr-2">Code</th>
                                  <th className="text-left py-1 pr-2">Description</th>
                                  <th className="text-right py-1 pr-2">Amount</th>
                                  <th className="text-right py-1">TDS ₹</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(block.lines || []).map((s) => (
                                  <tr
                                    key={`${block.major_head_code}-${s.payment_code}`}
                                    className="border-b border-border/50"
                                  >
                                    <td className="py-1 pr-2 font-mono">{s.code || s.payment_code}</td>
                                    <td className="py-1 pr-2 text-muted-foreground truncate max-w-[180px]">
                                      {s.description || s.label}
                                    </td>
                                    <td className="py-1 pr-2 text-right tabular-nums">
                                      {formatInr(s.amount ?? s.gross_amount)}
                                    </td>
                                    <td className="py-1 text-right tabular-nums font-medium">
                                      {formatInr(s.tds ?? s.tds_amount)}
                                    </td>
                                  </tr>
                                ))}
                                <tr className="border-t">
                                  <td colSpan={2} className="py-1 pr-2 font-medium">
                                    Total payable
                                  </td>
                                  <td className="py-1 pr-2 text-right tabular-nums font-medium">
                                    {formatInr(block.total_amount)}
                                  </td>
                                  <td className="py-1 text-right tabular-nums font-medium">
                                    {formatInr(block.total_tds)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : null}
            {calendar?.rules?.deposit ? (
              <p className="text-[10px] text-muted-foreground leading-relaxed">{calendar.rules.deposit}</p>
            ) : null}
          </div>

          <div className="rounded-lg border border-border/80 p-3 space-y-3 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">Quarterly return (Form 140)</p>
                <p className="text-[11px] text-muted-foreground">
                  Deductee-wise Excel for Form 140 filing
                </p>
              </div>
              {returnPreview?.status ? (
                <Pill tone={statusTone(returnPreview.status)}>{returnPreview.status}</Pill>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="text-[11px] text-muted-foreground">FY start</label>
                <select
                  className="block h-8 rounded-md border border-border bg-card px-2 text-xs min-w-[100px]"
                  value={returnFy}
                  onChange={(e) => setReturnFy(Number(e.target.value))}
                >
                  {fyOptions.map((y) => (
                    <option key={y} value={y}>
                      FY {y}-{(y + 1).toString().slice(2)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Quarter</label>
                <select
                  className="block h-8 rounded-md border border-border bg-card px-2 text-xs min-w-[90px]"
                  value={returnQuarter}
                  onChange={(e) => setReturnQuarter(Number(e.target.value))}
                >
                  <option value={1}>Q1 Apr–Jun</option>
                  <option value={2}>Q2 Jul–Sep</option>
                  <option value={3}>Q3 Oct–Dec</option>
                  <option value={4}>Q4 Jan–Mar</option>
                </select>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={excelBusy === "return"}
                onClick={downloadReturnExcel}
              >
                {excelBusy === "return" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                <span className="ml-1">Excel — return details</span>
              </Button>
            </div>
            {returnPreview ? (
              <div className="text-xs space-y-1.5">
                <p>
                  Due <span className="font-medium">{returnPreview.return_due_date}</span>
                  {" · "}
                  {returnPreview.period_start} → {returnPreview.period_end}
                </p>
                <p>
                  Deductees {returnPreview.applicable_count}
                  {" · "}TDS {formatInr(returnPreview.total_tds)}
                </p>
                {(returnPreview.tds_by_payment_code || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {returnPreview.tds_by_payment_code.map((c) => (
                      <Pill key={c.payment_code} tone="warning">
                        {c.payment_code}: {formatInr(c.tds_amount)}
                      </Pill>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No applicable TDS in this quarter.</p>
                )}
              </div>
            ) : null}
            {calendar?.rules?.return ? (
              <p className="text-[10px] text-muted-foreground leading-relaxed">{calendar.rules.return}</p>
            ) : null}
          </div>
        </div>

        {(calendar?.fy_quarters || []).length > 0 ? (
          <div className="mt-4 pt-3 border-t border-border/60">
            <p className="text-[11px] font-medium text-muted-foreground mb-2">This FY — quarter snapshot</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {calendar.fy_quarters.map((q) => (
                <button
                  key={q.quarter}
                  type="button"
                  className="rounded-md border border-border/70 px-2.5 py-2 text-left text-xs hover:bg-muted/40"
                  onClick={() => {
                    setReturnFy(q.fy_start_year);
                    setReturnQuarter(q.quarter);
                  }}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-medium">{q.quarter_label}</span>
                    <Pill tone={statusTone(q.status)}>{q.status}</Pill>
                  </div>
                  <p className="text-muted-foreground mt-1">Due {q.return_due_date}</p>
                  <p className="tabular-nums mt-0.5">{formatInr(q.total_tds)}</p>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </SectionCard>

      <SectionCard
        title="Payable TDS tags"
        subtitle="Map each payable code (commission, loading, freight…) to a TDS section. Limits & PAN apply from that section on finalize."
        testId="tds-payable-tags"
        action={
          canManage ? (
            <Button size="sm" disabled={!tagsDirty || tagsSaving} onClick={savePayableTags}>
              {tagsSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              <span className={tagsSaving ? "ml-1" : ""}>Save tags</span>
            </Button>
          ) : null
        }
      >
        {payableTags.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading tag catalog…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="text-left py-1.5 pr-2">Payable code</th>
                  <th className="text-left py-1.5 pr-2">Label</th>
                  <th className="text-left py-1.5 pr-2">TDS section</th>
                  <th className="text-left py-1.5">Notes</th>
                </tr>
              </thead>
              <tbody>
                {payableTags.map((row) => (
                  <tr key={row.payable_code} className="border-b border-border/50">
                    <td className="py-1.5 pr-2 font-mono">{row.payable_code}</td>
                    <td className="py-1.5 pr-2 text-muted-foreground">{row.label}</td>
                    <td className="py-1.5 pr-2">
                      {canManage ? (
                        <select
                          className="h-8 rounded-md border border-border bg-card px-2 text-xs min-w-[220px]"
                          value={row.tds_nature || "NONE"}
                          onChange={(e) => updatePayableTag(row.payable_code, e.target.value)}
                        >
                          {(tagOptions.length
                            ? tagOptions
                            : [
                                { value: "NONE", label: "No TDS" },
                                { value: "COMMISSION_1006", label: "Commission — 1006" },
                                { value: "FREIGHT_1023", label: "Freight — 1023/1024" },
                                { value: "CONTRACTOR_1023", label: "Contractor — 1023/1024" },
                                { value: "PURCHASE_1031", label: "Goods — 1031" },
                              ]
                          ).map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="font-mono">{row.tds_nature}</span>
                      )}
                    </td>
                    <td className="py-1.5 text-muted-foreground">
                      {row.seen_in_vouchers ? "Seen on vouchers" : row.is_default ? "System default" : ""}
                      {row.is_override ? " · saved" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-muted-foreground mt-2">
              Material goods (voucher base) always assess under 1031 when the buyer gate is on.
              Change a tag and click Save — new finalizations use the updated map.
            </p>
          </div>
        )}
      </SectionCard>

      {gapCount > 0 && (
        <SectionCard
          title="Unassessed sources (compliance gaps)"
          testId="tds-coverage-gaps"
          action={
            canManage ? (
              <Button size="sm" variant="outline" disabled={backfillAllBusy} onClick={backfillAllGaps}>
                {backfillAllBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                <span className={backfillAllBusy ? "ml-1" : ""}>Backfill all gaps</span>
              </Button>
            ) : null
          }
        >
          <div className="space-y-3 text-sm">
            {(coverage?.freight_gaps || []).length > 0 && (
              <div>
                <p className="font-medium mb-1">Freight (code 1023/1024)</p>
                <ul className="space-y-1">
                  {coverage.freight_gaps.map((g) => (
                    <li key={g.voucher_id} className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-mono">{g.code}</span>
                      <span className="text-muted-foreground">{g.party_name}</span>
                      <span>{formatInr(g.gross_amount || g.net_payable || 0)}</span>
                      {canManage && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={backfilling === g.voucher_id}
                          onClick={() => backfillFreight(g.voucher_id)}
                        >
                          {backfilling === g.voucher_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            "Backfill"
                          )}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {((coverage?.material_1031_gaps || coverage?.material_194q_gaps) || []).length > 0 && (
              <div>
                <p className="font-medium mb-1">Material purchases (code 1031) — re-finalize / settle to assess</p>
                <ul className="space-y-1">
                  {(coverage.material_1031_gaps || coverage.material_194q_gaps || []).slice(0, 20).map((g) => (
                    <li key={g.voucher_id} className="text-xs">
                      <span className="font-mono">{g.code}</span> · {g.party_name} · {formatInr(g.gross_amount || 0)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {((coverage?.labour_1023_gaps || coverage?.labour_194c_gaps) || []).length > 0 && (
              <div>
                <p className="font-medium mb-1">Labour batches (code 1023/1024)</p>
                <ul className="space-y-1">
                  {(coverage.labour_1023_gaps || coverage.labour_194c_gaps || []).slice(0, 20).map((g) => (
                    <li key={g.batch_id} className="text-xs">
                      Batch #{g.batch_id} · {g.work_date} · {g.party_name} · {formatInr(g.total_amount || 0)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Register" testId="tds-register-table">
        {canManage ? (
          <p className="text-[11px] text-muted-foreground mb-3">
            Use <strong>Reassess all FY</strong> after PAN updates or threshold crossings — not only when coverage gaps show.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2 mb-3 items-end">
          <div>
            <label className="text-[11px] text-muted-foreground">Payment code</label>
            <select
              className="block h-8 rounded-md border border-border bg-card px-2 text-xs min-w-[120px]"
              value={section}
              onChange={(e) => setSection(e.target.value)}
            >
              <option value="">All</option>
              {sectionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">PAN</label>
            <Input
              className="h-8 text-xs w-36"
              placeholder="Filter PAN"
              value={panFilter}
              onChange={(e) => setPanFilter(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs h-8">
            <input
              type="checkbox"
              checked={applicableOnly}
              onChange={(e) => setApplicableOnly(e.target.checked)}
            />
            Applicable only
          </label>
        </div>

        {loading && rows.length === 0 ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No TDS assessments in this FY yet.</p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs border-collapse min-w-[840px]">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-2 font-medium">Date</th>
                  <th className="py-2 pr-2 font-medium">Code</th>
                  <th className="py-2 pr-2 font-medium">Form</th>
                  <th className="py-2 pr-2 font-medium">Source</th>
                  <th className="py-2 pr-2 font-medium">Party / PAN</th>
                  <th className="py-2 pr-2 font-medium text-right">Gross</th>
                  <th className="py-2 pr-2 font-medium text-right">TDS %</th>
                  <th className="py-2 pr-2 font-medium text-right">TDS ₹</th>
                  <th className="py-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-muted/30">
                    <td className="py-1.5 pr-2 whitespace-nowrap">{r.entry_date}</td>
                    <td className="py-1.5 pr-2">
                      <Pill tone={r.tds_applicable ? "warning" : "default"}>
                        {r.payment_code || r.section || "—"}
                      </Pill>
                      {r.act_section ? (
                        <div className="text-[10px] text-muted-foreground">{r.act_section}</div>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-2 font-mono">{r.return_form || "140"}</td>
                    <td className="py-1.5 pr-2">
                      <div className="font-mono">{r.source_code || `#${r.source_id}`}</div>
                      <div className="text-[10px] text-muted-foreground">{r.source_kind}</div>
                    </td>
                    <td className="py-1.5 pr-2">
                      <div>{r.party_name || "—"}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{r.pan_no || "no PAN"}</div>
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{formatInr(r.gross_amount)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{r.tds_pct ? `${r.tds_pct}%` : "—"}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums font-medium">
                      {r.tds_applicable ? formatTdsInr(r.tds_amount) : "—"}
                    </td>
                    <td className="py-1.5 text-muted-foreground max-w-[220px]">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
